import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useSetRecoilState } from 'recoil';

import { suggestAllAIPlanStateAtom, SuggestAllAIPlanState } from '../recoil';
import { useTodoTasksCache } from '../queries';
import {
  appendTargetDatePlanRunResult,
  buildAllPlanHomeRestore,
  buildDailyPlanJobRecoveryState,
  buildPlanCacheEntryFromSnapshot,
  buildPlanCacheSnapshotForDate,
  findPendingPlanCacheJob,
  finishPendingPlanCacheJob,
  getPlanCacheForDate,
  readPlanCacheStore,
  resolvePlannedTypesFromCacheEntry,
  restorePlanCacheAfterPendingJob,
  shouldPersistPlanCacheSnapshot,
  writePlanCacheStore,
} from '../../helpers';
import {
  applyDailyPlanStreamEvent,
  DailyPlanStreamResult,
} from '../helpers/dailyPlanStreamEvents.helpers';
import { useJobPolling } from './useJobPolling';
import { JobNotFoundError } from '../api/jobs';
import {
  clearPersistedJob,
  clearPersistedJobIfMatches,
} from '../helpers/jobRecovery.helpers';
import {
  DAILY_PLAN_JOB_TYPE,
  DailyPlanJobContext,
  getPersistedDailyPlanJob,
} from './useSuggestAllAIPlan';

const emptyPlanState = (): SuggestAllAIPlanState => ({
  statusLines: [],
  schedules: [],
  summary: null,
  isLoading: false,
  acceptedIds: [],
  autoAcceptedIds: [],
  discardedIds: [],
  error: null,
  targetDate: null,
  lastRunTodoIds: [],
  lastRunRoutineIds: [],
});

/** Write the resumed run into the date cache before the job id is cleared, so
 * a later refresh shows this result instead of the plan it replaced. */
const persistRecoveredDailyPlan = (targetDate: string, state: SuggestAllAIPlanState): void => {
  const snapshot = buildPlanCacheSnapshotForDate(state, targetDate);
  if (!shouldPersistPlanCacheSnapshot(snapshot)) {
    return;
  }
  const existing = getPlanCacheForDate(readPlanCacheStore(), targetDate);
  const entry = buildPlanCacheEntryFromSnapshot({
    snapshot,
    plannedTypes: resolvePlannedTypesFromCacheEntry({
      lastRunAtISO: existing?.lastRunAtISO ?? new Date().toISOString(),
      signature: existing?.signature ?? '',
      schedules: snapshot.schedules,
      summary: snapshot.summary,
    }),
    signature: existing?.signature ?? '',
    lastRunTodoIds: state.lastRunTodoIds.length ? state.lastRunTodoIds : existing?.lastRunTodoIds,
    lastRunRoutineIds: state.lastRunRoutineIds.length
      ? state.lastRunRoutineIds
      : existing?.lastRunRoutineIds,
  });
  const store = readPlanCacheStore();
  writePlanCacheStore({
    version: 2,
    plansByDate: {
      ...store.plansByDate,
      [targetDate]: entry,
    },
  });
};

/**
 * Resumes a daily-plan generation job that was still running when the tab
 * reloaded or was closed. Mounted once at app root (AssistantChatProvider) —
 * reads the job_id persisted by useSuggestAllAIPlan's runSuggestAllAIPlan,
 * and if one exists, polls GET /api/v1/jobs/{job_id} and replays events
 * through the same applyDailyPlanStreamEvent parser a live stream uses.
 * The plan atom is not persisted, so mount state is empty and the date cache
 * still holds the previous job. Recovery strips that date before polling so
 * the old result is not shown in place of the run still generating.
 */
export const useDailyPlanJobRecovery = (): void => {
  const setSuggestAllAIPlanState = useSetRecoilState(suggestAllAIPlanStateAtom);
  const { findInCacheByDate } = useTodoTasksCache();
  const baselineRef = useRef<SuggestAllAIPlanState | null>(null);
  const targetDateRef = useRef<string | null>(null);
  const typesToReplaceRef = useRef<DailyPlanJobContext['typesToReplace']>(undefined);

  const [resumeJobId, setResumeJobId] = useState<string | null>(null);

  useEffect(() => {
    const persisted = getPersistedDailyPlanJob();
    const pendingCache = findPendingPlanCacheJob();
    const jobId = persisted?.jobId ?? pendingCache?.jobId;
    const targetDate = persisted?.context.targetDate ?? pendingCache?.date;
    if (!jobId || !targetDate) {
      return;
    }
    const today = dayjs().format('YYYY-MM-DD');
    const restore = buildAllPlanHomeRestore({
      store: readPlanCacheStore(),
      today,
      previousTargetDate: targetDate,
    });
    const cached: SuggestAllAIPlanState = {
      ...emptyPlanState(),
      ...restore.patch,
    };
    // Capture the baseline exactly once, before any poll event merges onto
    // it — mirrors the live path's fixed `strippedState` closure. Seed from
    // the date cache (other dates stay) then drop this run's target date so
    // the previous job's cards are not the thing on screen.
    const recovered = buildDailyPlanJobRecoveryState(
      cached,
      targetDate,
      persisted?.context.typesToReplace
    );
    baselineRef.current = recovered;
    targetDateRef.current = targetDate;
    typesToReplaceRef.current = persisted?.context.typesToReplace;
    setSuggestAllAIPlanState(recovered);
    setResumeJobId(jobId);
    // Mount-only: this hook has a single, fixed job to pick up per app load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useJobPolling({
    jobId: resumeJobId,
    onEvent: (event) => {
      const targetDate = targetDateRef.current;
      if (!targetDate || !baselineRef.current) {
        return;
      }
      applyDailyPlanStreamEvent(
        event.type,
        JSON.stringify(event.data),
        targetDate,
        findInCacheByDate(targetDate),
        {
          onStatusText: (text) => {
            if (!text) return;
            setSuggestAllAIPlanState((prev) => {
              if (prev.statusLines[prev.statusLines.length - 1] === text) return prev;
              return { ...prev, isLoading: true, statusLines: [...prev.statusLines, text].slice(-5) };
            });
          },
          onResult: (result: DailyPlanStreamResult) => {
            const baseline = baselineRef.current!;
            const merged = appendTargetDatePlanRunResult(
              baseline,
              result.schedules,
              result.summary,
              typesToReplaceRef.current
            );
            const keptIds = new Set(
              merged.schedules.map((schedule, index) => String(schedule.id ?? `plan-${index}`))
            );
            setSuggestAllAIPlanState((prev) => ({
              ...prev,
              // Stay loading until the job is done. Writing this partial into
              // the date cache would drop the hidden previous plan.
              isLoading: true,
              schedules: merged.schedules,
              summary: merged.summary,
              acceptedIds: prev.acceptedIds.filter((id) => keptIds.has(id)),
              autoAcceptedIds: prev.autoAcceptedIds.filter((id) => keptIds.has(id)),
              discardedIds: prev.discardedIds.filter((id) => keptIds.has(id)),
            }));
          },
        }
      );
    },
    onDone: () => {
      const targetDate = targetDateRef.current;
      let committed: SuggestAllAIPlanState | null = null;
      setSuggestAllAIPlanState((prev) => {
        committed = { ...prev, isLoading: false, statusLines: [] };
        return committed;
      });
      if (targetDate && committed) {
        persistRecoveredDailyPlan(targetDate, committed);
        finishPendingPlanCacheJob(targetDate);
      }
      clearPersistedJobIfMatches(DAILY_PLAN_JOB_TYPE, resumeJobId!);
      setResumeJobId(null);
    },
    onError: (_response, error) => {
      const targetDate = targetDateRef.current;
      clearPersistedJob(DAILY_PLAN_JOB_TYPE);
      setResumeJobId(null);
      if (targetDate && error instanceof JobNotFoundError) {
        restorePlanCacheAfterPendingJob(targetDate);
        const restore = buildAllPlanHomeRestore({
          store: readPlanCacheStore(),
          today: dayjs().format('YYYY-MM-DD'),
          previousTargetDate: targetDate,
        });
        setSuggestAllAIPlanState((prev) => ({
          ...prev,
          ...restore.patch,
          isLoading: false,
          statusLines: [],
          error: null,
        }));
        return;
      }
      if (targetDate) {
        finishPendingPlanCacheJob(targetDate);
      }
      setSuggestAllAIPlanState((prev) => ({
        ...prev,
        isLoading: false,
        statusLines: [],
        error: 'Something went wrong generating your plan.',
      }));
    },
    onCancelled: () => {
      setSuggestAllAIPlanState((prev) => ({ ...prev, isLoading: false, statusLines: [] }));
      if (resumeJobId) {
        clearPersistedJobIfMatches(DAILY_PLAN_JOB_TYPE, resumeJobId);
      }
      setResumeJobId(null);
    },
  });
};
